import { useEffect, useState } from "react";
import { onAuthStateChanged, User as FirebaseUser, setPersistence, browserLocalPersistence } from "firebase/auth";
// Importations correctes pour Firebase Firestore
import { doc, getDoc, setDoc, onSnapshot, serverTimestamp } from "firebase/firestore";
import { Timestamp } from "firebase/firestore";
import { auth, db } from "../services/firebase";
import { ADMIN_EMAILS } from "../services/auth";
import { User } from "../types/user";
import { useDispatch } from "react-redux";
import { setUser as setReduxUser, setError as setReduxError, setLoading as setReduxLoading } from "../store/slices/authSlice";
import { clearLocalRecentTrailers, getLocalRecentTrailers } from "../services/localStorageService";

// Fonction pour convertir les Timestamps en dates
const convertTimestamps = (obj: any): any => {
  if (obj === null || obj === undefined || typeof obj !== 'object') {
    return obj;
  }

  // Vérifier si l'objet est un Timestamp Firestore (a une méthode toDate)
  if (obj && typeof obj.toDate === 'function') {
    return obj.toDate().toISOString();
  }

  if (Array.isArray(obj)) {
    return obj.map(convertTimestamps);
  }

  const converted: any = {};
  for (const key in obj) {
    converted[key] = convertTimestamps(obj[key]);
  }
  return converted;
};

const createNewUser = (firebaseUser: FirebaseUser): User => {
  // Récupérer les bandes-annonces locales pour les migrer vers le nouveau compte
  const localTrailers = getLocalRecentTrailers();
  
  // Créer le nouvel utilisateur avec les bandes-annonces locales
  const newUser: User = {
    uid: firebaseUser.uid,
    email: firebaseUser.email || '',
    displayName: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || '',
    username: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || '',
    photoURL: firebaseUser.photoURL,
    role: ADMIN_EMAILS.includes(firebaseUser.email || '') ? 'admin' : 'user',
    recentTrailers: localTrailers,
    watchlist: { movies: [], tvShows: [] },
    favorites: [],
    history: [],
    settings: {
      darkMode: false,
      notifications: true,
      language: 'fr',
      autoplayTrailers: true,
      privacy: 'public'
    }
  };

  // Effacer les bandes-annonces locales après la migration
  clearLocalRecentTrailers();

  return newUser;
};

export const useAuth = () => {
  const dispatch = useDispatch();

  const initializeAuth = async () => {
    try {
      await setPersistence(auth, browserLocalPersistence);
      console.log("Persistance de l'authentification configurée avec succès");
    } catch (error) {
      console.error("Erreur lors de la configuration de la persistance:", error);
    }
  };

  useEffect(() => {
    initializeAuth();

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      dispatch(setReduxLoading(true));

      try {
        if (firebaseUser) {
          const userDocRef = doc(db, "users", firebaseUser.uid);
          const userDoc = await getDoc(userDocRef);
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const user: User = {
              uid: firebaseUser.uid,
              email: firebaseUser.email || "",
              displayName: firebaseUser.displayName || "",
              username: userData.username || firebaseUser.email?.split('@')[0] || "",
              photoURL: firebaseUser.photoURL,
              role: ADMIN_EMAILS.includes(firebaseUser.email || "") ? 'admin' : 'user',
              settings: userData.settings || {
                darkMode: false,
                notifications: true,
                language: 'fr',
                privacy: 'public',
                autoplayTrailers: true
              },
              recentTrailers: userData.recentTrailers || [],
              watchlist: userData.watchlist || { movies: [], tvShows: [] },
              favorites: userData.favorites || [],
              history: userData.history || [],
              createdAt: userData.createdAt || new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            };
            dispatch(setReduxUser(user));
          } else {
            const newUser: User = {
              uid: firebaseUser.uid,
              email: firebaseUser.email || "",
              displayName: firebaseUser.displayName || "",
              username: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || "",
              photoURL: firebaseUser.photoURL,
              role: ADMIN_EMAILS.includes(firebaseUser.email || "") ? 'admin' : 'user',
              settings: {
                darkMode: false,
                notifications: true,
                language: 'fr',
                privacy: 'public',
                autoplayTrailers: true
              },
              recentTrailers: getLocalRecentTrailers(),
              watchlist: { movies: [], tvShows: [] },
              favorites: [],
              history: [],
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            };
            await setDoc(userDocRef, newUser);
            dispatch(setReduxUser(newUser));
            clearLocalRecentTrailers();
          }
        } else {
          dispatch(setReduxUser(null));
        }
      } catch (error) {
        console.error("Erreur lors de la gestion de l'authentification:", error);
        dispatch(setReduxError(error instanceof Error ? error.message : "Une erreur est survenue"));
      } finally {
        dispatch(setReduxLoading(false));
      }
    });

    return () => unsubscribe();
  }, [dispatch]);

  return {
    initialize: initializeAuth
  };
};
